
'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import type { ArtItem, Artist } from '@/types';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn, getArtworkImageUrl, getImageUrlVariant, shouldBypassNextImageOptimization } from '@/lib/utils';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Skeleton } from '../ui/skeleton';

interface LightboxProps {
  images: string[];
  artists: Artist[];
  moreArtworks: ArtItem[];
  startIndex?: number;
  onClose: () => void;
}

export function Lightbox({
  images,
  artists,
  moreArtworks,
  startIndex = 0,
  onClose,
}: LightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const nextImage = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const artistLinks = artists.length > 0 ? artists.map((artist) => (
      <Link key={artist.id} href={`/artists/${artist.slug}`} className="hover:underline">
        {artist.name}
      </Link>
  )).flatMap((link, index) => index === 0 ? [link] : [', ', link]) : null;
  const currentImageUrl = getImageUrlVariant(images[currentIndex], 'public');

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex animate-in fade-in-0">
        {/* Main Content Area */}
        <div className="relative flex-1 h-full">
            <div className="absolute inset-0 flex items-center justify-center p-4 md:p-16">
                 <Image
                    src={currentImageUrl}
                    alt={`Artwork image ${currentIndex + 1}`}
                    fill
                    className="object-contain"
                    sizes="(max-width: 768px) 100vw, calc(100vw - 350px)"
                    unoptimized={shouldBypassNextImageOptimization(currentImageUrl)}
                />
            </div>
           
            {images.length > 1 && (
                <>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/30 hover:bg-black/50 text-white"
                    >
                        <ChevronLeft className="h-8 w-8" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/30 hover:bg-black/50 text-white"
                    >
                        <ChevronRight className="h-8 w-8" />
                    </Button>
                </>
            )}
        </div>

         {/* Right Sidebar */}
        <div className="hidden md:block h-full w-[350px] bg-background/80 backdrop-blur-lg border-l border-border/20 p-6 overflow-y-auto flex-shrink-0">
            <h3 className="text-xl font-headline mb-1">More by this Artist</h3>
            {artistLinks && (
                 <div className="text-sm text-muted-foreground mb-6">{artistLinks}</div>
            )}
            <div className="space-y-4">
                {moreArtworks.length > 0 ? (
                    moreArtworks.map(artwork => {
                        const imageUrl = getArtworkImageUrl(artwork, 'sm');

                        return (
                        <Link href={`/artworks/${artwork.id}`} key={artwork.id} className="group block" onClick={onClose}>
                            <Card className="flex items-center gap-4 p-3 hover:bg-muted/50 transition-colors">
                                <div className="w-16 h-16 relative rounded-md overflow-hidden bg-muted flex-shrink-0">
                                   <Image 
                                        src={imageUrl}
                                        alt={artwork.title}
                                        fill
                                        sizes="64px"
                                        className="object-cover"
                                        unoptimized={shouldBypassNextImageOptimization(imageUrl)}
                                   />
                                </div>
                                <div>
                                    <p className="font-semibold leading-tight group-hover:underline">{artwork.title}</p>
                                    <p className="text-xs text-muted-foreground">{artwork.artistNames.join(', ')}</p>
                                </div>
                            </Card>
                        </Link>
                        );
                    })
                ) : (
                   <div className="text-center text-muted-foreground text-sm py-8">
                        No other public artworks by this artist found.
                   </div>
                )}
            </div>
        </div>
        
        {/* Close Button is always on top */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute top-4 right-4 h-12 w-12 rounded-full bg-black/30 hover:bg-black/50 text-white z-10"
        >
          <X className="h-8 w-8" />
        </Button>
    </div>
  );
}
